import { useState, memo } from 'react';
import { cn } from '@/lib/utils';
import { FileText, Wrench, CheckCircle2, XCircle, AlertTriangle, Cpu, Hammer } from 'lucide-react';

/**
 * Structurally compatible with both builders' VerboseEvent types.
 * Consumers pass their own VerboseEvent arrays — no import coupling.
 */
export interface TimelineVerboseEvent {
  type: 'system_prompt' | 'tool_call' | 'tool_result' | 'model_info' | 'tools_available';
  timestamp: string;
  data: {
    prompt?: string;
    toolName?: string;
    toolInput?: unknown;
    toolResult?: unknown;
    isError?: boolean;
    model?: string;
    intent?: string;
    tools?: Array<{ name: string; description: string }>;
  };
}

/** Parsed structure from a tool result string */
export interface ParsedToolResult {
  primaryMessage: string;
  validationWarnings: string[];
  editBudgetWarning: string | null;
  verificationWarning: string | null;
  editCount: number | null;
}

/** Parse a tool result string into structured parts */
export function parseToolResultString(resultStr: string): ParsedToolResult {
  const parsed: ParsedToolResult = {
    primaryMessage: '',
    validationWarnings: [],
    editBudgetWarning: null,
    verificationWarning: null,
    editCount: null,
  };

  if (!resultStr) return parsed;

  const lines = resultStr.split('\n');
  const primaryLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.includes('unknown prop') || line.includes('missing required prop')) {
      parsed.validationWarnings.push(line.trim());
      i++;
      continue;
    }

    const editBudgetMatch = line.match(/^WARNING: You have made (\d+) edits/);
    if (editBudgetMatch) {
      parsed.editBudgetWarning = line.trim();
      parsed.editCount = parseInt(editBudgetMatch[1], 10);
      i++;
      continue;
    }

    if (line.match(/^WARNING: You have made \d+ changes without/)) {
      parsed.verificationWarning = line.trim();
      i++;
      continue;
    }

    primaryLines.push(line);
    i++;
  }

  parsed.primaryMessage = primaryLines.join('\n').trim();
  return parsed;
}

/** Check if a tool_result event has warnings (successful but with issues) */
export function hasWarnings(event: TimelineVerboseEvent): boolean {
  if (event.type !== 'tool_result') return false;
  if (event.data.isError) return false;

  const result = event.data.toolResult as Record<string, unknown> | undefined;
  if (!result || !result.success) return false;

  const resultStr = typeof result.result === 'string' ? result.result : '';
  if (!resultStr) return false;

  const parsed = parseToolResultString(resultStr);
  return (
    parsed.validationWarnings.length > 0 ||
    parsed.editBudgetWarning !== null ||
    parsed.verificationWarning !== null
  );
}

// ---------------------------------------------------------------------------
// Icon helper
// ---------------------------------------------------------------------------

function getStepIcon(event: TimelineVerboseEvent, isError: boolean, isWarning: boolean) {
  switch (event.type) {
    case 'system_prompt':
      return <FileText className="h-3.5 w-3.5 text-blue-500" />;
    case 'model_info':
      return <Cpu className="h-3.5 w-3.5 text-purple-500" />;
    case 'tools_available':
      return <Hammer className="h-3.5 w-3.5 text-teal-500" />;
    case 'tool_call':
      return <Wrench className="h-3.5 w-3.5 text-orange-500" />;
    case 'tool_result':
      if (isError) return <XCircle className="h-3.5 w-3.5 text-red-500" />;
      if (isWarning) return <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />;
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
  }
}

// ---------------------------------------------------------------------------
// Title / summary / content helpers (ported from VerboseEventDisplay)
// ---------------------------------------------------------------------------

function getTitle(event: TimelineVerboseEvent) {
  switch (event.type) {
    case 'system_prompt':
      return 'System Prompt';
    case 'model_info':
      return 'Model';
    case 'tools_available':
      return `Tools (${event.data.tools?.length || 0})`;
    case 'tool_call':
      return `Tool: ${event.data.toolName}`;
    case 'tool_result':
      return `Result: ${event.data.toolName}${event.data.isError ? ' (Error)' : ''}`;
  }
}

function getSummary(event: TimelineVerboseEvent) {
  switch (event.type) {
    case 'system_prompt': {
      const len = event.data.prompt?.length ?? 0;
      return len > 0 ? `${len.toLocaleString()} characters` : null;
    }
    case 'tools_available': {
      const tools = event.data.tools;
      if (!tools || tools.length === 0) return null;
      return tools.map((t) => t.name).join(', ');
    }
    case 'model_info': {
      const model = event.data.model || '';
      if (model.includes('opus')) return 'Opus 4.5 (Deep thinking)';
      if (model.includes('sonnet')) return 'Sonnet 4';
      if (model.includes('haiku')) return 'Haiku 3.5';
      return model;
    }
    case 'tool_call': {
      const input = event.data.toolInput as Record<string, unknown> | undefined;
      if (!input) return null;
      const toolName = event.data.toolName;
      if (toolName === 'edit_ui' && input.target) {
        const changes = input.changes as Record<string, unknown> | undefined;
        const ops = changes ? Object.keys(changes).join(', ') : '';
        return ops ? `target: ${input.target}, ${ops}` : `target: ${input.target}`;
      }
      if (toolName === 'add_component') {
        const type = input.type || '';
        const parentId = input.parentId || 'root';
        return type ? `${type} → ${parentId}` : null;
      }
      if (toolName === 'add_action') {
        const id = input.id || '';
        const type = input.type || '';
        return id ? `${id} (${type})` : null;
      }
      if (toolName === 'advance_build_step' && input.stepId) {
        return `stepId: ${input.stepId}`;
      }
      if (toolName === 'write_file' && input.path) {
        return `path: ${input.path}`;
      }
      if (toolName === 'read_file' && input.path) {
        return `path: ${input.path}`;
      }
      if (input.target) return `target: ${input.target}`;
      if (input.name) return `name: ${input.name}`;
      return null;
    }
    case 'tool_result': {
      const result = event.data.toolResult as Record<string, unknown> | undefined;
      if (!result) return null;
      if (result.error) {
        const errStr = String(result.error);
        if (errStr.startsWith('BLOCKED')) return 'BLOCKED';
        if (errStr.includes('verification FAILED')) {
          const attemptMatch = errStr.match(/attempt (\d+\/\d+)/);
          return attemptMatch ? `verification failed (${attemptMatch[1]})` : 'verification failed';
        }
        return errStr.length > 60 ? errStr.slice(0, 57) + '...' : errStr;
      }
      if (result.success) {
        const resultStr = typeof result.result === 'string' ? result.result : '';
        if (!resultStr) return 'success';
        const parsed = parseToolResultString(resultStr);
        const parts: string[] = [];
        if (parsed.validationWarnings.length > 0) {
          parts.push(
            `${parsed.validationWarnings.length} warning${parsed.validationWarnings.length > 1 ? 's' : ''}`,
          );
        }
        if (parsed.editCount !== null) {
          parts.push(`edit ${parsed.editCount}/12`);
        }
        if (parsed.verificationWarning) {
          parts.push('needs verification');
        }
        return parts.length > 0 ? `success (${parts.join(', ')})` : 'success';
      }
      return null;
    }
  }
}

function getContent(event: TimelineVerboseEvent) {
  switch (event.type) {
    case 'system_prompt':
      return event.data.prompt || '';
    case 'model_info':
      return `Model: ${event.data.model}\nIntent: ${event.data.intent}`;
    case 'tools_available': {
      const tools = event.data.tools;
      if (!tools || tools.length === 0) return 'No tools available';
      return tools.map((t) => `${t.name}\n  ${t.description}`).join('\n\n');
    }
    case 'tool_call':
      return JSON.stringify(event.data.toolInput, null, 2);
    case 'tool_result': {
      const result = event.data.toolResult as Record<string, unknown> | undefined;
      if (!result) return '';

      if (result.error) return String(result.error);

      const resultStr = typeof result.result === 'string' ? result.result : '';
      if (!resultStr) return JSON.stringify(result, null, 2);

      const parsed = parseToolResultString(resultStr);
      const sections: string[] = [];

      if (parsed.primaryMessage) {
        sections.push(parsed.primaryMessage);
      }
      if (parsed.validationWarnings.length > 0) {
        sections.push(
          `--- Validation Warnings (${parsed.validationWarnings.length}) ---\n` +
            parsed.validationWarnings.map((w) => `  \u26A0 ${w}`).join('\n'),
        );
      }
      if (parsed.editBudgetWarning) {
        sections.push(`--- Edit Budget ---\n  \u26A0 ${parsed.editBudgetWarning}`);
      }
      if (parsed.verificationWarning) {
        sections.push(`--- Verification ---\n  \u26A0 ${parsed.verificationWarning}`);
      }
      return sections.join('\n\n');
    }
  }
}

// ---------------------------------------------------------------------------
// Inline warning lines (shown under summary for warnings without expanding)
// ---------------------------------------------------------------------------

function getInlineWarnings(event: TimelineVerboseEvent): string[] {
  if (event.type !== 'tool_result') return [];
  const result = event.data.toolResult as Record<string, unknown> | undefined;
  if (!result || !result.success) return [];
  const resultStr = typeof result.result === 'string' ? result.result : '';
  if (!resultStr) return [];

  const parsed = parseToolResultString(resultStr);
  const lines: string[] = [];
  for (const w of parsed.validationWarnings) lines.push(w);
  if (parsed.editBudgetWarning) lines.push(parsed.editBudgetWarning);
  if (parsed.verificationWarning) lines.push(parsed.verificationWarning);
  return lines;
}

// ---------------------------------------------------------------------------
// VerboseTimelineStep
// ---------------------------------------------------------------------------

interface StepProps {
  event: TimelineVerboseEvent;
  stepNumber: number;
  isLast: boolean;
  isPending?: boolean;
}

const VerboseTimelineStep = memo(function VerboseTimelineStep({
  event,
  stepNumber,
  isLast,
  isPending,
}: StepProps) {
  const isError = event.type === 'tool_result' && event.data.isError === true;
  const isWarning = !isError && hasWarnings(event);
  const [detailsOpen, setDetailsOpen] = useState(isError);

  const summary = getSummary(event);
  const inlineWarnings = isWarning ? getInlineWarnings(event) : [];

  const badgeColor = isError
    ? 'bg-red-500 text-white'
    : isWarning
      ? 'bg-yellow-500 text-white'
      : 'bg-muted text-muted-foreground';

  const contentBg = isError
    ? 'bg-red-500/5 border-red-500/20'
    : isWarning
      ? 'bg-yellow-500/5 border-yellow-500/20'
      : 'bg-muted/30 border-border/30';

  return (
    <div
      className={cn(
        'relative flex gap-3',
        isPending && 'animate-in fade-in slide-in-from-left-2 duration-300',
      )}
      data-testid="timeline-step"
    >
      {/* Vertical line */}
      {!isLast && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border" />}

      {/* Step number badge */}
      <div
        className={cn(
          'flex-shrink-0 w-[27px] h-[27px] rounded-full flex items-center justify-center text-[10px] font-bold',
          badgeColor,
        )}
        data-testid="step-number"
      >
        {stepNumber}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        {/* Header: icon + title + summary */}
        <div className="flex items-start gap-1.5 text-xs font-mono min-h-[27px] flex-wrap">
          <span className="flex items-center gap-1.5 shrink-0 h-[27px]">
            {getStepIcon(event, isError, isWarning)}
            <span className="font-semibold">{getTitle(event)}</span>
          </span>
          {summary && (
            <span
              className={cn(
                'break-words',
                isError
                  ? 'text-red-400'
                  : isWarning
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-muted-foreground',
              )}
              data-testid="step-summary"
            >
              — {summary}
            </span>
          )}
        </div>

        {/* Inline warnings (always visible when isWarning) */}
        {inlineWarnings.length > 0 && (
          <div className="mt-0.5">
            {inlineWarnings.map((w, i) => (
              <p key={i} className="text-[10px] text-yellow-600 dark:text-yellow-400 font-mono">
                {w}
              </p>
            ))}
          </div>
        )}

        {/* Show/Hide details toggle */}
        <button
          onClick={() => setDetailsOpen(!detailsOpen)}
          className="text-[10px] text-muted-foreground hover:text-foreground mt-0.5"
          data-testid="details-toggle"
        >
          {detailsOpen ? 'Hide details' : 'Show details'}
        </button>

        {/* Detail content */}
        {detailsOpen && (
          <pre
            className={cn(
              'mt-1 p-2 text-xs font-mono border rounded overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap',
              contentBg,
            )}
          >
            {getContent(event)}
          </pre>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// VerboseTimeline
// ---------------------------------------------------------------------------

export interface VerboseTimelineProps {
  events: TimelineVerboseEvent[];
  isPending?: boolean;
  className?: string;
}

export function VerboseTimeline({ events, isPending, className }: VerboseTimelineProps) {
  if (events.length === 0) return null;

  return (
    <div className={cn('my-2', className)} data-testid="verbose-timeline">
      {events.map((event, index) => (
        <VerboseTimelineStep
          key={`${event.timestamp}-${event.type}-${index}`}
          event={event}
          stepNumber={index + 1}
          isLast={index === events.length - 1}
          isPending={isPending}
        />
      ))}
    </div>
  );
}
